import { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useModalAberto } from '../contexts/ModalContext';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmtData = iso => iso ? format(parseISO(iso), 'dd/MM/yyyy', { locale: ptBR }) : '—';
const fmtKm   = km  => km  ? Number(km).toLocaleString('pt-BR') + ' km' : '—';

const STATUS_COR = {
  vencido: { bg: 'bg-red-50 border-red-200',    badge: 'bg-red-100 text-red-700',    dot: 'bg-red-500',    label: 'Vencido'  },
  proximo: { bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', label: 'Próximo'  },
};

const REGRAS = [
  { value: 'mensal',      label: 'Mensal'      },
  { value: 'trimestral',  label: 'Trimestral'  },
  { value: 'semestral',   label: 'Semestral'   },
  { value: 'pre_feriado', label: 'Pré-Feriado' },
  { value: 'data_fixa',   label: 'Data Fixa'   },
];

const FERIADOS = [
  { label: 'Carnaval',     data: '02-28' },
  { label: 'Páscoa',       data: '03-31' },
  { label: 'Dia das Mães', data: '05-11' },
  { label: 'Dia dos Pais', data: '08-10' },
  { label: 'Independência', data: '09-07' },
  { label: 'Natal',        data: '12-25' },
  { label: 'Ano Novo',     data: '01-01' },
];

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Alertas() {
  const [aba, setAba] = useState('recorrentes');
  const [totais, setTotais] = useState({ vencidos: 0, proximos: 0, enviados: 0 });

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">🔔 Alertas</h1>
        {(totais.vencidos > 0 || totais.proximos > 0 || totais.enviados > 0) && (
          <div className="flex gap-2 text-sm flex-wrap">
            {totais.vencidos > 0 && (
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">
                {totais.vencidos} vencido{totais.vencidos > 1 ? 's' : ''}
              </span>
            )}
            {totais.proximos > 0 && (
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-medium">
                {totais.proximos} próximo{totais.proximos > 1 ? 's' : ''}
              </span>
            )}
            {totais.enviados > 0 && (
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                {totais.enviados} enviado{totais.enviados > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setAba('recorrentes')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${aba === 'recorrentes' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          🔄 Serviços Recorrentes
        </button>
        <button
          onClick={() => setAba('campanhas')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${aba === 'campanhas' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          📣 Campanhas
        </button>
      </div>

      {aba === 'recorrentes' && <AbaRecorrentes onTotaisChange={setTotais} />}
      {aba === 'campanhas'   && <AbaCampanhas />}
    </div>
  );
}

// ─── Aba 1: Serviços Recorrentes ──────────────────────────────────────────────
// Calcula dias restantes até a próxima data (negativo = vencido)
function diasParaData(proximaData) {
  if (!proximaData) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(proximaData); alvo.setHours(0, 0, 0, 0);
  return Math.round((alvo - hoje) / 86400000);
}

function labelAlerta(alerta) {
  const dias = diasParaData(alerta.proximaData);
  // Vencido pela data
  if (dias !== null && dias < 0)  return { texto: `Vencido há ${Math.abs(dias)} dia${Math.abs(dias) !== 1 ? 's' : ''}`, cor: 'text-red-600 font-bold' };
  if (dias !== null && dias === 0) return { texto: 'Vence hoje!', cor: 'text-red-600 font-bold' };
  if (dias !== null && dias === 1) return { texto: 'Vence amanhã', cor: 'text-orange-600 font-bold' };
  // Vencido pelo KM (data ainda no futuro)
  if (alerta.status === 'vencido' && alerta.proximoKm) return { texto: 'KM excedido', cor: 'text-red-600 font-bold' };
  if (dias !== null) return { texto: `Faltam ${dias} dias`, cor: dias <= 7 ? 'text-orange-500 font-semibold' : 'text-yellow-600 font-semibold' };
  return null;
}

function GrupoAlertas({ titulo, cor, icone, itens, enviados, onWhatsApp, onMarcar }) {
  const [aberto, setAberto] = useState(true);
  if (itens.length === 0) return null;
  return (
    <div className="rounded-xl border overflow-hidden shadow-sm">
      <button
        onClick={() => setAberto(a => !a)}
        className={`w-full flex items-center justify-between px-4 py-3 ${cor} text-left`}
      >
        <span className="font-semibold text-sm flex items-center gap-2">
          {icone} {titulo}
          <span className="bg-white/60 text-gray-700 text-xs px-2 py-0.5 rounded-full font-bold">{itens.length}</span>
        </span>
        <span className="text-lg">{aberto ? '▾' : '▸'}</span>
      </button>

      {aberto && (
        <div className="bg-white divide-y divide-gray-50">
          {itens.map(({ veiculo, cliente, alerta }) => {
            const label = labelAlerta(alerta);
            const jaEnviado = enviados[alerta.servicoId];
            return (
              <div key={alerta.servicoId} className={`p-4 transition-colors ${jaEnviado ? 'bg-blue-50' : ''}`}>
                {/* Linha principal */}
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-800 text-sm">{alerta.nomeServico}</span>
                      {jaEnviado && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">✓ Enviado</span>}
                    </div>
                    <div className="text-xs text-gray-500 font-medium">{veiculo.marca} {veiculo.modelo} • {veiculo.placa}</div>
                    <div className="text-xs text-gray-400">{cliente?.nome} {cliente?.telefone ? `• ${cliente.telefone}` : ''}</div>
                  </div>

                  {/* Dias em destaque */}
                  {label && (
                    <div className={`text-xs shrink-0 text-right ${label.cor}`}>{label.texto}</div>
                  )}
                </div>

                {/* Detalhes colapsados */}
                <div className="mt-2 grid grid-cols-2 gap-x-4 text-xs text-gray-500 ml-0">
                  <div>
                    <span className="text-gray-400">Última OS:</span>
                    <div className="text-gray-600">#{alerta.ultimaOsNum} — {fmtData(alerta.dataNaOS)}</div>
                    {alerta.kmNaOS && <div>{fmtKm(alerta.kmNaOS)}</div>}
                  </div>
                  <div>
                    <span className="text-gray-400">Próxima revisão:</span>
                    {alerta.proximaData && <div className="text-gray-700 font-medium">{fmtData(alerta.proximaData)}</div>}
                    {alerta.proximoKm  && <div className="text-gray-700 font-medium">{fmtKm(alerta.proximoKm)}</div>}
                    {!alerta.proximaData && !alerta.proximoKm && <div>—</div>}
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => onWhatsApp(cliente, veiculo, alerta)}
                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  >📱 WhatsApp</button>
                  <button
                    onClick={() => !jaEnviado && onMarcar(alerta.servicoId)}
                    disabled={jaEnviado}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      jaEnviado ? 'bg-blue-100 text-blue-600 cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >{jaEnviado ? '✓ Marcado' : 'Marcar enviado'}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AbaRecorrentes({ onTotaisChange }) {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horizonte, setHorizonte] = useState(7); // 0=hoje, 7, 30, null=todos
  const [mostrarEnviados, setMostrarEnviados] = useState(false);
  const [enviados, setEnviados] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alertas_enviados') || '{}'); }
    catch { return {}; }
  });

  const marcarEnviado = (servicoId) => {
    setEnviados(prev => {
      const novo = { ...prev, [servicoId]: true };
      localStorage.setItem('alertas_enviados', JSON.stringify(novo));
      return novo;
    });
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/alertas/recorrentes');
      setDados(r.data);
    } catch { toast.error('Erro ao carregar alertas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Achatar todos os alertas em lista plana com contexto de veículo/cliente
  const todosAlertas = [];
  dados.forEach(({ veiculo, cliente, alertas }) => {
    alertas.forEach(alerta => todosAlertas.push({ veiculo, cliente, alerta }));
  });

  const totalEnviados = todosAlertas.filter(({ alerta }) => enviados[alerta.servicoId]).length;
  const pendentes = todosAlertas.filter(({ alerta }) => !enviados[alerta.servicoId]);

  // Contadores globais (sem filtro de horizonte)
  const nVencidos = pendentes.filter(({ alerta }) => alerta.status === 'vencido').length;
  const nProximos = pendentes.filter(({ alerta }) => alerta.status === 'proximo').length;

  useEffect(() => {
    if (onTotaisChange) onTotaisChange({ vencidos: nVencidos, proximos: nProximos, enviados: totalEnviados });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, enviados]);

  // Filtra por horizonte
  const dentroHorizonte = ({ alerta }) => {
    if (horizonte === null) return true;
    // Vencidos (por data ou KM) sempre aparecem — já requerem ação
    if (alerta.status === 'vencido') return true;
    const dias = diasParaData(alerta.proximaData);
    if (dias === null) return true;
    return dias <= horizonte;
  };

  const alertasFiltrados = pendentes.filter(dentroHorizonte);

  // Determina grupo real de cada alerta
  const grupoReal = ({ alerta }) => {
    // Vencido por data ou por KM → sempre ação imediata
    if (alerta.status === 'vencido') return 'vencidos';
    const dias = diasParaData(alerta.proximaData);
    if (dias !== null && dias <= 7) return 'semana';
    return 'mes';
  };

  const grupos = {
    vencidos: alertasFiltrados.filter(i => grupoReal(i) === 'vencidos'),
    semana:   alertasFiltrados.filter(i => grupoReal(i) === 'semana'),
    mes:      alertasFiltrados.filter(i => grupoReal(i) === 'mes'),
  };

  // Alertas enviados (separados)
  const alertasEnviados = todosAlertas.filter(({ alerta }) => enviados[alerta.servicoId]);

  const enviarWhatsAppServico = (cliente, veiculo, alerta) => {
    const num = cliente?.telefone?.replace(/\D/g, '');
    if (!num) return toast.error('Cliente sem telefone cadastrado');
    const msg =
      `Olá ${cliente.nome}!\n\n` +
      `Passando para avisar sobre o seu ${veiculo.marca} ${veiculo.modelo} (${veiculo.placa}):\n\n` +
      `Baseado no histórico de reparo, verificar a necessidade de troca das peças/serviços:\n` +
      `• ${alerta.nomeServico}\n\n` +
      `Agende uma visita! Estamos à disposição.`;
    window.open(`https://wa.me/55${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Carregando...</div>;

  const temAlerta = grupos.vencidos.length + grupos.semana.length + grupos.mes.length > 0;

  return (
    <div className="space-y-4">
      {/* Contadores */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Vencidos',  valor: nVencidos,    cor: 'bg-red-50 border-red-200 text-red-700' },
          { label: 'Próximos',  valor: nProximos,    cor: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
          { label: 'Enviados',  valor: totalEnviados, cor: 'bg-blue-50 border-blue-200 text-blue-700' },
        ].map(({ label, valor, cor }) => (
          <div key={label} className={`border rounded-xl p-4 text-center ${cor}`}>
            <div className="text-3xl font-bold">{valor}</div>
            <div className="text-sm font-medium mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Filtro de horizonte */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 font-medium">Mostrar alertas:</span>
        {[
          { val: 0,    label: 'Hoje' },
          { val: 7,    label: '7 dias' },
          { val: 30,   label: '30 dias' },
          { val: null, label: 'Todos' },
        ].map(({ val, label }) => (
          <button
            key={String(val)}
            onClick={() => setHorizonte(val)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              horizonte === val
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
            }`}
          >{label}</button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{alertasFiltrados.length} alerta{alertasFiltrados.length !== 1 ? 's' : ''} no período</span>
      </div>

      {/* Grupos */}
      {!temAlerta ? (
        <div className="text-center py-16 text-gray-400">
          Nenhum alerta pendente no período selecionado.
        </div>
      ) : (
        <>
          <GrupoAlertas
            titulo="Ação imediata — Vencidos"
            cor="bg-red-50 border-b border-red-200 text-red-800"
            icone="🔴"
            itens={grupos.vencidos}
            enviados={enviados}
            onWhatsApp={enviarWhatsAppServico}
            onMarcar={marcarEnviado}
          />
          <GrupoAlertas
            titulo="Esta semana — até 7 dias"
            cor="bg-orange-50 border-b border-orange-200 text-orange-800"
            icone="🟠"
            itens={grupos.semana}
            enviados={enviados}
            onWhatsApp={enviarWhatsAppServico}
            onMarcar={marcarEnviado}
          />
          <GrupoAlertas
            titulo="Este mês — 8 a 30 dias"
            cor="bg-yellow-50 border-b border-yellow-200 text-yellow-800"
            icone="🟡"
            itens={grupos.mes}
            enviados={enviados}
            onWhatsApp={enviarWhatsAppServico}
            onMarcar={marcarEnviado}
          />
        </>
      )}

      {/* Enviados colapsável */}
      {totalEnviados > 0 && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setMostrarEnviados(a => !a)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
          >
            <span className="text-sm font-medium text-gray-500 flex items-center gap-2">
              ✓ Enviados
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-bold">{totalEnviados}</span>
            </span>
            <span className="text-gray-400 text-sm">{mostrarEnviados ? '▾' : '▸'}</span>
          </button>
          {mostrarEnviados && (
            <div className="bg-white divide-y divide-gray-50">
              {alertasEnviados.map(({ veiculo, cliente, alerta }) => (
                <div key={alerta.servicoId} className="px-4 py-3 flex items-center gap-3 bg-blue-50/40">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-700">{alerta.nomeServico}</div>
                    <div className="text-xs text-gray-400">{veiculo.marca} {veiculo.modelo} • {veiculo.placa} • {cliente?.nome}</div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium shrink-0">✓ Enviado</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex gap-2">
        <span>ℹ️</span>
        <span>Alertas detectados automaticamente pelas OS concluídas. Apenas veículos com histórico de serviços aparecem aqui.</span>
      </div>
    </div>
  );
}

// ─── Aba 2: Campanhas ─────────────────────────────────────────────────────────
function AbaCampanhas() {
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos'); // todos | pendente | enviada | inativa
  const [modal, setModal] = useState(null); // null | { modo, campanha? }
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [envioModal, setEnvioModal] = useState(null); // campanha sendo disparada
  useModalAberto(!!modal || !!envioModal);
  const [enviados, setEnviados] = useState({}); // { clienteId: dataISO } — carregado do banco
  const [destinatarios, setDestinatarios] = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/alertas/campanhas');
      setCampanhas(r.data);
    } catch { toast.error('Erro ao carregar campanhas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirCriar = () => {
    setForm({ nome: '', mensagem: '', regra: 'mensal', feriado: '', dataFixa: '', ativa: true });
    setModal({ modo: 'criar' });
  };

  const abrirEditar = c => {
    setForm({ nome: c.nome, mensagem: c.mensagem, regra: c.regra, feriado: c.feriado || '', dataFixa: c.dataFixa || '', ativa: c.ativa !== false });
    setModal({ modo: 'editar', campanha: c });
  };

  const salvar = async () => {
    if (!form.nome || !form.mensagem) return toast.error('Preencha nome e mensagem');
    setSalvando(true);
    try {
      if (modal.modo === 'criar') { await api.post('/alertas/campanhas', form); toast.success('Campanha criada'); }
      else { await api.put(`/alertas/campanhas/${modal.campanha._id}`, form); toast.success('Campanha atualizada'); }
      setModal(null); carregar();
    } catch { toast.error('Erro ao salvar campanha'); }
    finally { setSalvando(false); }
  };

  const deletar = async (id) => {
    if (!window.confirm('Remover esta campanha?')) return;
    try { await api.delete(`/alertas/campanhas/${id}`); toast.success('Campanha removida'); carregar(); }
    catch { toast.error('Erro ao remover'); }
  };

  const abrirEnvio = async (campanha) => {
    try {
      const r = await api.get('/alertas/campanhas/destinatarios');
      setDestinatarios(r.data);
      // Carrega histórico de envios já salvos no banco
      const historico = {};
      (campanha.envios || []).forEach(e => { historico[e.clienteId] = e.data; });
      setEnviados(historico);
      setEnvioModal(campanha);
    } catch { toast.error('Erro ao carregar destinatários'); }
  };

  const enviarParaCliente = (cliente, campanha) => {
    const num = cliente.telefone?.replace(/\D/g, '');
    if (!num) return;
    const msg = campanha.mensagem.replace('{nome}', cliente.nome);
    window.open(`https://wa.me/55${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const marcarClienteEnviado = async (clienteId) => {
    const now = new Date().toISOString();
    setEnviados(prev => ({ ...prev, [clienteId]: now }));
    const cliente = destinatarios.find(d => d._id === clienteId);
    try {
      await api.post(`/alertas/campanhas/${envioModal._id}/envio`, {
        clienteId,
        clienteNome: cliente?.nome || '',
      });
    } catch { /* marcação local já ocorreu */ }
  };

  const fmtRegra = c => {
    if (c.regra === 'mensal')      return 'Mensal';
    if (c.regra === 'trimestral')  return 'Trimestral';
    if (c.regra === 'semestral')   return 'Semestral';
    if (c.regra === 'pre_feriado') return `Pré-Feriado${c.feriado ? ` — ${c.feriado}` : ''}`;
    if (c.regra === 'data_fixa')   return `Data fixa${c.dataFixa ? ` — ${format(parseISO(c.dataFixa + 'T00:00:00'), 'dd/MM/yyyy')}` : ''}`;
    return c.regra;
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Carregando...</div>;

  const totalPendente = campanhas.filter(c => c.ativa !== false && !c.ultimoEnvio).length;
  const totalEnviada  = campanhas.filter(c => !!c.ultimoEnvio).length;
  const totalInativa  = campanhas.filter(c => c.ativa === false).length;

  const campanhasFiltradas = filtro === 'todos' ? campanhas
    : filtro === 'pendente' ? campanhas.filter(c => c.ativa !== false && !c.ultimoEnvio)
    : filtro === 'enviada'  ? campanhas.filter(c => !!c.ultimoEnvio)
    : campanhas.filter(c => c.ativa === false);

  return (
    <div className="space-y-4">
      {/* Contadores */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { key: 'pendente', label: 'Pendentes', valor: totalPendente, cor: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
          { key: 'enviada',  label: 'Enviadas',  valor: totalEnviada,  cor: 'bg-blue-50 border-blue-200 text-blue-700' },
          { key: 'inativa',  label: 'Inativas',  valor: totalInativa,  cor: 'bg-gray-50 border-gray-200 text-gray-600' },
        ].map(({ key, label, valor, cor }) => (
          <button key={key}
            onClick={() => setFiltro(filtro === key ? 'todos' : key)}
            className={`border rounded-xl p-4 text-center transition-all ${cor} ${filtro === key ? 'ring-2 ring-offset-1 ring-current' : 'opacity-80 hover:opacity-100'}`}
          >
            <div className="text-3xl font-bold">{valor}</div>
            <div className="text-sm font-medium mt-1">{label}</div>
          </button>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Campanhas de mensagens para clientes via WhatsApp.</p>
        <button onClick={abrirCriar} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          + Nova Campanha
        </button>
      </div>

      {/* Dica de variável */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex gap-2">
        <span>💡</span>
        <span>Use <code className="bg-blue-100 px-1 rounded">{'{nome}'}</code> na mensagem para personalizar com o nome do cliente.</span>
      </div>

      {campanhasFiltradas.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          {filtro === 'todos' ? 'Nenhuma campanha cadastrada.' : `Nenhuma campanha ${filtro === 'pendente' ? 'pendente' : filtro === 'enviada' ? 'enviada' : 'inativa'}.`}
        </div>
      ) : (
        <div className="space-y-3">
          {campanhasFiltradas.map(c => {
            const inativa = c.ativa === false;
            return (
              <div key={c._id} className={`bg-white rounded-xl border shadow-sm p-4 ${inativa ? 'border-l-4 border-l-gray-300 border-gray-100' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-800">{c.nome}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inativa ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                        {inativa ? 'Inativa' : 'Ativa'}
                      </span>
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{fmtRegra(c)}</span>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-2 mb-2">{c.mensagem}</p>
                    {inativa && (
                      <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-500 mb-2">
                        <span>⏸</span>
                        <span>Campanha pausada — ative para disparar novamente</span>
                      </div>
                    )}
                    {c.ultimoEnvio && (
                      <p className="text-xs text-gray-400">Último envio: {fmtData(c.ultimoEnvio)}</p>
                    )}
                    {(c.envios || []).length > 0 && (
                      <p className="text-xs text-green-600 font-medium">✓ {c.envios.length} cliente{c.envios.length > 1 ? 's' : ''} notificado{c.envios.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => !inativa && abrirEnvio(c)}
                      disabled={inativa}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${inativa ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                    >
                      📱 Disparar
                    </button>
                    <button onClick={() => abrirEditar(c)} className="border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50">Editar</button>
                    <button onClick={() => deletar(c._id)} className="text-red-400 hover:text-red-600 px-3 py-1.5 rounded-lg text-xs">Remover</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal criar/editar campanha */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-800">
              {modal.modo === 'criar' ? 'Nova Campanha' : 'Editar Campanha'}
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome da campanha *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Revisão de Verão"
                value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem *</label>
              <textarea
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder={`Olá {nome}! Aproveite nossa promoção de revisão completa. Entre em contato e agende já!`}
                value={form.mensagem}
                onChange={e => setForm(f => ({ ...f, mensagem: e.target.value }))}
              />
              <p className="text-xs text-gray-400 mt-1">Use <code>{'{nome}'}</code> para personalizar.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Regra de envio</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.regra}
                onChange={e => setForm(f => ({ ...f, regra: e.target.value }))}
              >
                {REGRAS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {form.regra === 'pre_feriado' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feriado</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.feriado}
                  onChange={e => setForm(f => ({ ...f, feriado: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  {FERIADOS.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                </select>
              </div>
            )}

            {form.regra === 'data_fixa' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.dataFixa}
                  onChange={e => setForm(f => ({ ...f, dataFixa: e.target.value }))}
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativa"
                checked={form.ativa !== false}
                onChange={e => setForm(f => ({ ...f, ativa: e.target.checked }))}
                className="w-4 h-4 accent-blue-600"
              />
              <label htmlFor="ativa" className="text-sm text-gray-700">Campanha ativa</label>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(null)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={salvar} disabled={salvando} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de disparo */}
      {envioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEnvioModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-800">Disparar: {envioModal.nome}</h2>
            <p className="text-sm text-gray-500">Clique em cada cliente para abrir o WhatsApp com a mensagem pré-preenchida.</p>

            <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 max-h-28 overflow-y-auto whitespace-pre-wrap border border-gray-200">
              {envioModal.mensagem}
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {destinatarios.map(d => (
                <div key={d._id} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-colors ${enviados[d._id] ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                  <button
                    onClick={() => enviarParaCliente(d, envioModal)}
                    className="flex-1 text-left"
                  >
                    <div className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                      {enviados[d._id] && <span className="text-green-600 text-xs font-bold">✓</span>}
                      {d.nome}
                    </div>
                    <div className="text-xs text-gray-400">
                      {d.telefone}
                      {enviados[d._id] && (
                        <span className="ml-2 text-green-600">
                          enviado em {format(new Date(enviados[d._id]), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => enviarParaCliente(d, envioModal)}
                    title="Abrir WhatsApp"
                    className="text-green-500 hover:text-green-700 text-lg px-1"
                  >📱</button>
                  <button
                    onClick={() => marcarClienteEnviado(d._id)}
                    title={enviados[d._id] ? 'Marcar novo envio' : 'Marcar como enviado'}
                    className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      enviados[d._id]
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {enviados[d._id] ? '✓ Reenviar' : 'Marcar'}
                  </button>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <button onClick={() => { setEnvioModal(null); carregar(); }} className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
